import { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { CodePanel } from "./ArticleCodeBlock";
import { ArticleTerminalLine } from "../content/articles";

const TYPE_MS = 22;
const AFTER_COMMAND_MS = 340;
const OUTPUT_LINE_MS = 38;

const blink = keyframes`
    0%, 49% { opacity: 1; }
    50%, 100% { opacity: 0; }
`;

const Screen = styled.div`
    position: relative;
`;

/* The finished transcript, kept invisible, so the panel is its final height
   from the start and the page never reflows while lines are still arriving. */
const Ghost = styled.div`
    visibility: hidden;
`;

const Live = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
`;

const Line = styled.div`
    white-space: pre;
    min-height: 1.7em;
`;

const Prompt = styled.span`
    color: ${({ theme }) => theme.colour.COLOUR_LIGHT};
    user-select: none;
`;

const Command = styled.span`
    color: ${({ theme }) => theme.colour.OFF_WHITE};
`;

const Output = styled.span`
    color: ${({ theme }) => theme.colour.OFF_WHITE};
    opacity: 0.78;
`;

const Cursor = styled.span<{ $idle?: boolean }>`
    display: inline-block;
    width: 0.55em;
    height: 1.05em;
    margin-left: 0.05em;
    vertical-align: text-bottom;
    background-color: ${({ theme }) => theme.colour.COLOUR_LIGHT};
    animation: ${({ $idle }) => ($idle ? blink : "none")} 1.1s step-end infinite;
`;

const Replay = styled.button`
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
    background: transparent;
    border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
    border-radius: 2px;
    padding: 0.15rem 0.55rem;
    cursor: pointer;
    opacity: 0.8;
    white-space: nowrap;

    &:hover {
        opacity: 1;
        border-color: ${({ theme }) => theme.colour.COLOUR_LIGHT};
        color: ${({ theme }) => theme.colour.COLOUR_LIGHT};
    }

    &:disabled {
        cursor: default;
        opacity: 0.35;
    }
`;

type Position = { line: number; chars: number };

type ArticleTerminalProps = {
    title?: string;
    lines: ArticleTerminalLine[];
};

function TranscriptLine({ line, chars, cursor }: { line: ArticleTerminalLine; chars?: number; cursor?: boolean }) {
    const text = chars === undefined ? line.text : line.text.slice(0, chars);

    if (line.kind === "command") {
        return (
            <Line>
                <Prompt>$ </Prompt>
                <Command>{text}</Command>
                {cursor && <Cursor />}
            </Line>
        );
    }

    return (
        <Line>
            <Output>{text}</Output>
        </Line>
    );
}

export default function ArticleTerminal({ title = "terminal", lines }: ArticleTerminalProps) {
    const screenRef = useRef<HTMLDivElement>(null);
    const reducedMotion = useMemo(
        () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
        []
    );
    const [armed, setArmed] = useState(reducedMotion);
    const [run, setRun] = useState(0);
    const [position, setPosition] = useState<Position>(
        reducedMotion ? { line: lines.length, chars: 0 } : { line: 0, chars: 0 }
    );

    const finished = position.line >= lines.length;

    useEffect(() => {
        const screen = screenRef.current;
        if (!screen || armed) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setArmed(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.35 }
        );
        observer.observe(screen);
        return () => observer.disconnect();
    }, [armed]);

    useEffect(() => {
        if (!armed || reducedMotion) return;

        let timer = 0;
        let cancelled = false;

        const step = (line: number, chars: number) => {
            if (cancelled) return;
            setPosition({ line, chars });

            if (line >= lines.length) return;

            const current = lines[line];
            if (current.kind === "command") {
                if (chars < current.text.length) {
                    timer = window.setTimeout(() => step(line, chars + 1), TYPE_MS);
                } else {
                    timer = window.setTimeout(() => step(line + 1, 0), AFTER_COMMAND_MS);
                }
                return;
            }

            timer = window.setTimeout(() => step(line + 1, 0), OUTPUT_LINE_MS);
        };

        step(0, 0);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [armed, run, lines, reducedMotion]);

    const replay = (
        <Replay
            type="button"
            onClick={() => setRun((count) => count + 1)}
            disabled={!finished || reducedMotion}
            aria-label="Run again"
        >
            run again
        </Replay>
    );

    return (
        <CodePanel title={title} action={replay} bodyAsCode>
            <Screen ref={screenRef}>
                <Ghost aria-hidden>
                    {lines.map((line, index) => (
                        <TranscriptLine key={index} line={line} />
                    ))}
                    <Line />
                </Ghost>
                <Live>
                    {lines.slice(0, position.line).map((line, index) => (
                        <TranscriptLine key={index} line={line} />
                    ))}
                    {!finished && (
                        <TranscriptLine
                            line={lines[position.line]}
                            chars={lines[position.line].kind === "command" ? position.chars : 0}
                            cursor={lines[position.line].kind === "command"}
                        />
                    )}
                    {finished && (
                        <Line>
                            <Prompt>$ </Prompt>
                            <Cursor $idle />
                        </Line>
                    )}
                </Live>
            </Screen>
        </CodePanel>
    );
}
